import { useMemo, type ReactNode } from "react";
import { Box, Column, Text, UserIcon } from "@hightouch/htui";
import { PhoneFrame } from "./phone-frame";

// Add 45min to current time so the preview looks slightly random but relevant
const PREVIEW_TIME_OFFSET_MS = 45 * 60 * 1000;

function usePreviewTimestamp() {
  return useMemo(() => {
    const d = new Date(Date.now() + PREVIEW_TIME_OFFSET_MS);
    return d.toLocaleString("en-US", {
      weekday: "long",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, []);
}

export function SmsPreview({
  body,
  children,
}: {
  body?: string;
  /** Rich bubble content (e.g. the canvas's block spans) — wins over `body`. */
  children?: ReactNode;
}) {
  const timestamp = usePreviewTimestamp();

  return (
    <PhoneFrame>
      <Column gap={2}>
        <Column
          align="center"
          gap={2}
          bg="base.lightBackground"
          p={3}
          borderBottomWidth="hairline"
          borderBottomStyle="solid"
          borderColor="base.border"
        >
          <Box
            w="40px"
            h="40px"
            borderRadius="full"
            bg="base.backgroundDisabled"
            display="flex"
            alignItems="center"
            justifyContent="center"
            shadow="buttonShadow"
            flexShrink={0}
          >
            <Box as={UserIcon} boxSize={4} color="text.tertiary" />
          </Box>
        </Column>

        <Column p={2} gap={2}>
          <Box alignSelf="center">
            <Text size="sm" color="text.tertiary">
              {timestamp}
            </Text>
          </Box>

          <Box
            bg="base.background"
            py={2}
            px={3}
            borderRadius="3xl"
            w="90%"
            alignSelf="flex-start"
            position="relative"
            _before={{
              content: '""',
              position: "absolute",
              zIndex: 0,
              bottom: 0,
              left: "-7px",
              height: "20px",
              width: "20px",
              bg: "base.background",
              borderBottomRightRadius: "15px",
            }}
            _after={{
              content: '""',
              position: "absolute",
              zIndex: 1,
              bottom: 0,
              left: "-10px",
              width: "10px",
              height: "20px",
              bg: "white",
              borderBottomRightRadius: "10px",
            }}
          >
            {children ?? <Text>{body || "Your message here…"}</Text>}
          </Box>
        </Column>
      </Column>
    </PhoneFrame>
  );
}
